import { navigate, RouteComponentProps } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './StockEquipmentForm.module.scss';
import { Input } from '../Input/Input';
import { escapeHtml, logger } from '../../utils';
import { Button } from '../Button/Button';
import { api } from '../../state/effects';
import {
    DocumentType,
    Document,
    Region,
    StockEquipmentStatus,
    EngineType,
    MauLocation,
} from '../../../interfaces/customer-api';
import { useOvermindState } from '../../state';
import { Spinner } from '../Spinner/Spinner';
import { CompleteRequestDialog } from '../CompleteRequestDialog/CompleteRequestDialog';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { MenuItem, styled } from '@mui/material';
import CloseIcon from '../../assets/close.svg';
import { Dropdown } from '../DropDown/DropDown';
import Chevron from '../../assets/chevron-left.svg';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LoadingOverlay } from '../LoadingOverlay/LoadingOverlay';
import dayjs from 'dayjs';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { EquipmentType } from '../../../interfaces/internal';

interface FileDocument {
    file: File;
    documentId?: number;
    key?: string;
}

export const DocumentName: React.FC<{
    files: FileDocument[];
    onClick: (documentToRemove: FileDocument) => void;
}> = ({ files, onClick }) => {
    return (
        <div className={css.documentNameContainer}>
            {files.map((file) => (
                <div key={file.file.name} className={css.documentName}>
                    {file.file.name}
                    <button type="button" onClick={() => onClick(file)}>
                        <CloseIcon />
                    </button>
                </div>
            ))}
        </div>
    );
};

export const StockEquipmentForm: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation('StockEquipment');
    const { user, selectedStockEquipment } = useOvermindState();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [configurationNumber, setConfigurationNumber] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<string>('');
    const [engineType, setEngineType] = useState<EngineType | ''>('');
    const [mauLocation, setMauLocation] = useState<MauLocation | ''>('');
    const [model, setModel] = useState('');
    const [estimatedAvailability, setEstimatedAvailability] = useState('');
    const [manufacturingYear, setManufacturingYear] = useState(new Date().getFullYear());
    const [specification, setSpecification] = useState<FileDocument[]>([]);
    const [dataSheet, setDataSheet] = useState<FileDocument[]>([]);
    const [pictures, setPictures] = useState<FileDocument[]>([]);
    const [region, setRegion] = useState<Region[]>([]);
    const [price, setPrice] = useState<number | null>(null);
    const [currency, setCurrency] = useState<string | null>(null);
    const [contactEmail, setContactEmail] = useState<string>('');
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string>('');
    const [isRegionSelectOpen, setIsRegionSelectOpen] = useState(false);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFilesCount, setTotalFilesCount] = useState(0);
    const [removedDocumentKeys, setRemovedDocumentKeys] = useState<string[]>([]);
    const [isImageSizeErrorDialogOpen, setIsImageSizeErrorDialogOpen] = useState(false);
    const [priceBlurred, setPriceBlurred] = useState(false);

    const MAX_IMAGE_SIZE_BYTES = 8 * 1024 * 1024; // 8 MB

    const equipmentTypes = Object.values(EquipmentType).sort();

    // Add useEffect to populate form fields when editing or duplicating (Copy and create new).
    useEffect(() => {
        if (selectedStockEquipment) {
            setConfigurationNumber(selectedStockEquipment.configurationNumber || '');
            setDescription(selectedStockEquipment.description || '');
            setType(selectedStockEquipment.type || '');
            setEngineType(selectedStockEquipment.engineType || '');
            setMauLocation(selectedStockEquipment.mauLocation || '');
            setModel(selectedStockEquipment.model || '');
            setEstimatedAvailability(
                selectedStockEquipment.estimatedAvailability
                    ? new Date(selectedStockEquipment.estimatedAvailability).toISOString().split('T')[0]
                    : '',
            );
            setManufacturingYear(selectedStockEquipment.manufacturingYear || new Date().getFullYear());
            setRegion(Array.isArray(selectedStockEquipment.region) ? selectedStockEquipment.region : []);
            setPrice(selectedStockEquipment.price ?? null);
            setCurrency(selectedStockEquipment.currency ?? null);
            setContactEmail(selectedStockEquipment.contactEmail ?? '');

            // Load documents only for editing
            if (selectedStockEquipment.id) {
                const loadDocuments = async () => {
                    try {
                        const documents = await api.getDocuments(selectedStockEquipment.documentKeys || []);

                        documents.forEach((doc) => {
                            const fd: FileDocument = {
                                file: { name: doc.name } as File,
                                documentId: doc.id,
                                key: doc.key,
                            };
                            if (doc.type === DocumentType.Specification) {
                                setSpecification((prev) => [...prev, fd]);
                            } else if (doc.type === DocumentType.DataSheet) {
                                setDataSheet((prev) => [...prev, fd]);
                            } else if (doc.type === DocumentType.Picture) {
                                setPictures((prev) => [...prev, fd]);
                            }
                        });
                    } catch (error) {
                        logger.error('Error loading documents:', error);
                    }
                };
                loadDocuments();
            }
        }
    }, [selectedStockEquipment]);

    const handleTypeChange = (value: string) => {
        setType(value);
    };

    const handleDocumentRemove = (
        documentToRemove: FileDocument,
        setState: React.Dispatch<React.SetStateAction<FileDocument[]>>,
    ) => {
        setState((prev) => prev.filter((fd) => fd !== documentToRemove));

        // If this is an existing document (has a DB key), queue it for deletion on submit
        if (documentToRemove.key !== undefined) {
            setRemovedDocumentKeys((prev) => [...prev, documentToRemove.key as string]);
        }
    };

    const handleDocumentAdd = (
        event: React.ChangeEvent<HTMLInputElement>,
        setState: React.Dispatch<React.SetStateAction<FileDocument[]>>,
        maxFileSizeBytes?: number,
    ) => {
        const file = event.target.files;

        if (file) {
            if (maxFileSizeBytes) {
                const oversizedFiles = Array.from(file).filter((f) => f.size > maxFileSizeBytes);
                if (oversizedFiles.length > 0) {
                    setIsImageSizeErrorDialogOpen(true);
                    event.target.value = '';
                    return;
                }
            }
            const newEntries: FileDocument[] = Array.from(file).map((f) => ({ file: f }));
            setState((prev) => [...prev, ...newEntries]);
        }
        event.target.value = '';
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Reset the error dialog value
        setShowErrorDialog(false);
        setIsButtonDisabled(true);
        setShowSpinner(true);
        setUploadProgress('');

        if (user) {
            try {
                if (selectedStockEquipment && selectedStockEquipment.id) {
                    // Delete removed documents first
                    if (removedDocumentKeys.length > 0) {
                        setUploadProgress('Deleting removed documents...');
                        await api.deleteDocument(removedDocumentKeys.join(','));
                        logger.info('Removed documents deleted:', removedDocumentKeys);
                    }
                }
                // Collect only new files for upload (existing documents already have a key, skip them)
                const newSpecification = specification.filter((file) => !file.key);
                const newDataSheet = dataSheet.filter((file) => !file.key);
                const newPictures = pictures.filter((file) => !file.key);
                const allNewEntries: FileDocument[] = [...newSpecification, ...newDataSheet, ...newPictures];
                const allFiles: File[] = allNewEntries.map((file) => file.file);
                let uploadedKeys: string[] = [];

                // Keys of existing documents still present (not removed by user)
                const existingKeys = [...specification, ...dataSheet, ...pictures]
                    .filter((fd) => fd.key)
                    .map((fd) => fd.key as string);

                if (allFiles.length > 0) {
                    setUploadProgress('Getting upload URLs...');

                    // Get upload presigned URLs for all files
                    const fileNames = allFiles.map((file) => file.name);
                    const uploadData = await api.getPresignedUrls(fileNames, 'stock-equipment');

                    if (!uploadData) {
                        throw new Error('Failed to get upload presigned URLs');
                    }

                    setUploadProgress('Uploading files to S3...');

                    // Upload files to S3 using presigned URLs
                    setTotalFilesCount(allFiles.length);
                    const uploadPromises = allFiles.map(async (file, index) => {
                        const { uploadUrl, key } = uploadData[index];
                        setCurrentFileIndex(index + 1);
                        setUploadProgress(`Uploading ${file.name}...`);
                        const success = await api.uploadFileToS3(file, uploadUrl);
                        if (!success) {
                            throw new Error(`Failed to upload file: ${file.name}`);
                        }
                        return key;
                    });

                    uploadedKeys = await Promise.all(uploadPromises);
                    logger.info('Files uploaded successfully to S3:', uploadedKeys);

                    setUploadProgress('Creating document records...');

                    // Create document records in the database
                    const documents = uploadedKeys.map((key, index) => {
                        const file = allNewEntries[index];
                        let type = DocumentType.Other;

                        if (newSpecification.includes(file)) {
                            type = DocumentType.Specification;
                        } else if (newDataSheet.includes(file)) {
                            type = DocumentType.DataSheet;
                        } else if (newPictures.includes(file)) {
                            type = DocumentType.Picture;
                        }

                        return {
                            key,
                            type,
                            name: file.file.name,
                        } as Document;
                    });

                    const documentIds = await api.createDocuments(documents);
                    if (!documentIds) {
                        throw new Error('Failed to create document records');
                    }
                }
                const allDocumentKeys = [...existingKeys, ...uploadedKeys];

                const requestData = {
                    configurationNumber: configurationNumber,
                    description: description,
                    type: type,
                    engineType: engineType as EngineType,
                    mauLocation: mauLocation as MauLocation,
                    model: model,
                    estimatedAvailability: estimatedAvailability ? new Date(estimatedAvailability) : undefined,
                    documentKeys: allDocumentKeys,
                    region: region,
                    status: selectedStockEquipment?.status ?? StockEquipmentStatus.Active,
                    manufacturingYear: manufacturingYear,
                    price: price,
                    currency: price != null ? currency : null,
                    contactEmail: contactEmail.trim() !== '' ? contactEmail.trim() : null,
                };
                // Send machine info to stock equipment api with document IDs
                // If selectedStockEquipment has an id, update the existing record, otherwise create a new one
                const request =
                    selectedStockEquipment && selectedStockEquipment.id
                        ? await api.updateStockEquipment(selectedStockEquipment.id, requestData)
                        : await api.insertStockEquipment(requestData);

                if (request !== null) {
                    setIsCompleteDialogOpen(true);
                } else {
                    setShowErrorDialog(true);
                    setIsCompleteDialogOpen(true);
                }
            } catch (error) {
                setShowErrorDialog(true);
                setIsCompleteDialogOpen(true);
                logger.error('Error sending service request:', error);
            }
        } else {
            logger.info('No customer selected');
        }

        setShowSpinner(false);
        setIsButtonDisabled(false);
        setUploadProgress('');
    };

    // Check if all required fields are filled
    useEffect(() => {
        // Check that year has 4 numbers
        const isYearValid = manufacturingYear !== undefined && manufacturingYear.toString().length === 4;
        const isPriceValid = price == null || currency != null;
        const isEmailValid = !contactEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail);
        const allFilled =
            [
                configurationNumber,
                description,
                type,
                engineType,
                mauLocation,
                model,
                estimatedAvailability,
                manufacturingYear,
                region,
                specification,
                dataSheet,
                pictures,
            ].every((val) => val !== undefined && val.toString().trim() !== '') &&
            isYearValid &&
            isPriceValid &&
            isEmailValid;
        setIsButtonDisabled(!allFilled);
    }, [
        configurationNumber,
        description,
        model,
        type,
        engineType,
        mauLocation,
        estimatedAvailability,
        manufacturingYear,
        specification,
        dataSheet,
        pictures,
        region,
        price,
        currency,
        contactEmail,
    ]);

    const handleRegionChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event;

        if (value === 'all' || (Array.isArray(value) && value.includes('all'))) {
            // If all regions are already selected, deselect everything
            // Otherwise, select all regions
            if (region.length === Object.values(Region).length) {
                setRegion([]);
            } else {
                setRegion(Object.values(Region));
            }
        } else {
            // Otherwise, set the selected regions
            setRegion(typeof value === 'string' ? (value.split(',') as Region[]) : (value as Region[]));
        }
    };

    const isAllRegionsSelected = region.length === Object.values(Region).length;

    return (
        <>
            <form id="stock-equipment-form" onSubmit={handleSubmit}>
                <div className={css.formContainer}>
                    <div className={css.formHead}>{t('Details')}</div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Configuration number')}
                        <Input
                            inputProps={{
                                value: configurationNumber || '',
                                onChange: ({ target }) => setConfigurationNumber(escapeHtml(target.value)),
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Description')}
                        <Input
                            inputProps={{
                                value: description,
                                onChange: ({ target }) => setDescription(escapeHtml(target.value)),
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Type')}
                        <Dropdown
                            className={css.typeDropdown}
                            handleChange={handleTypeChange}
                            label={''}
                            options={equipmentTypes.map((type) => ({
                                id: type,
                                label: type,
                            }))}
                            selected={type}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Model')}
                        <Input
                            inputProps={{
                                value: model,
                                onChange: ({ target }) => {
                                    setModel(escapeHtml(target.value));
                                },
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Engine type')}
                        <Dropdown
                            className={css.typeDropdown}
                            handleChange={(value) => setEngineType(value)}
                            label={''}
                            options={Object.values(EngineType).map((value) => ({
                                id: value,
                                label: value,
                            }))}
                            selected={engineType}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('MAU location')}
                        <Dropdown
                            className={css.typeDropdown}
                            handleChange={(value) => setMauLocation(value)}
                            label={''}
                            options={Object.values(MauLocation).map((value) => ({
                                id: value,
                                label: value,
                            }))}
                            selected={mauLocation}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Estimated availability')}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <StyledDatePicker
                                value={estimatedAvailability ? dayjs(estimatedAvailability) : null}
                                slotProps={{
                                    leftArrowIcon: { sx: { width: '2rem', height: '2rem' } },
                                    rightArrowIcon: { sx: { width: '2rem', height: '2rem' } },
                                    monthButton: { sx: { fontSize: '1.6rem' } },
                                    yearButton: { sx: { fontSize: '1.6rem' } },
                                    day: { sx: { fontSize: '1.6rem' } },
                                }}
                                onChange={(newValue) => setEstimatedAvailability(newValue?.format('YYYY-MM-DD') ?? '')}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Manufacturing year')}
                        <Input
                            inputProps={{
                                type: 'number',
                                value: manufacturingYear || '',
                                onChange: ({ target }) => {
                                    const value = target.value.replace(/[^0-9]/g, '');
                                    setManufacturingYear(Number(value));
                                },
                                placeholder: 'YYYY',
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Regions')}
                        <StyledSelect
                            multiple
                            value={region}
                            onChange={handleRegionChange}
                            onOpen={() => setIsRegionSelectOpen(true)}
                            onClose={() => setIsRegionSelectOpen(false)}
                            IconComponent={() => <StyledChevron isOpen={isRegionSelectOpen} />}
                            renderValue={() => region.join(', ')}
                        >
                            <StyledMenuItem key="all" value="all">
                                {isAllRegionsSelected ? (
                                    <CheckBoxIcon
                                        fontSize="medium"
                                        style={{ marginRight: 8, boxSizing: 'content-box' }}
                                    />
                                ) : (
                                    <CheckBoxOutlineBlankIcon
                                        fontSize="medium"
                                        style={{ marginRight: 8, boxSizing: 'content-box' }}
                                    />
                                )}
                                {t('All')}
                            </StyledMenuItem>
                            {Object.values(Region).map((regionValue) => {
                                const isSelected = region.includes(regionValue);

                                return (
                                    <StyledMenuItem key={regionValue} value={regionValue}>
                                        {isSelected ? (
                                            <CheckBoxIcon
                                                fontSize="medium"
                                                style={{ marginRight: 8, boxSizing: 'content-box' }}
                                            />
                                        ) : (
                                            <CheckBoxOutlineBlankIcon
                                                fontSize="medium"
                                                style={{ marginRight: 8, boxSizing: 'content-box' }}
                                            />
                                        )}
                                        {regionValue}
                                    </StyledMenuItem>
                                );
                            })}
                        </StyledSelect>
                    </div>
                    <div className={css.formInput}>
                        {t('Price')}
                        <div
                            className={css.priceContainer}
                            onFocus={() => setPriceBlurred(false)}
                            onBlur={(e) => {
                                // Only set blurred when focus leaves the entire price section
                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                    setPriceBlurred(true);
                                }
                            }}
                        >
                            <div className={css.priceInputWrapper}>
                                <Input
                                    inputProps={{
                                        type: 'number',
                                        step: 'any',
                                        value: price ?? '',
                                        onChange: ({ target }) => {
                                            if (target.value === '') {
                                                setPrice(null);
                                            } else if (Number(target.value) > 0) {
                                                setPrice(Number(target.value));
                                            }
                                            // negative values are ignored
                                        },
                                    }}
                                />
                            </div>
                            <div className={css.currencySelectWrapper}>
                                <Dropdown
                                    className={css.typeDropdown}
                                    handleChange={(value: string) => setCurrency(value || null)}
                                    label=""
                                    options={[
                                        { id: '', label: t('Currency') },
                                        { id: 'USD', label: '$ / USD' },
                                        { id: 'EUR', label: '€ / EUR' },
                                    ]}
                                    selected={currency ?? ''}
                                />
                            </div>
                        </div>
                        {/* Show hint only after user leaves the price section without selecting a currency */}
                        {price != null && !currency && priceBlurred && (
                            <span className={css.priceHint}>
                                {t('Please select a currency for the price')}
                            </span>
                        )}
                    </div>
                    <div className={css.formInput}>
                        {t('Contact email')}
                        <Input
                            inputProps={{
                                type: 'email',
                                value: contactEmail,
                                onChange: ({ target }) => setContactEmail(target.value),
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <div className={css.documentsText}>{t('Documents')}</div>
                        <div className={css.dropdownContainer}>
                            {specification.length > 0 && (
                                <DocumentName
                                    files={specification}
                                    onClick={(file) => handleDocumentRemove(file, setSpecification)}
                                />
                            )}
                            <span className={css.star}>*</span>
                            {t('Specification')}
                            <div className={css.dropdown}>
                                <input
                                    type="file"
                                    id="specification-file-input"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleDocumentAdd(e, setSpecification)}
                                    multiple={true}
                                    accept='.pdf'
                                />
                                <Button
                                    id="specification-upload-button"
                                    type="button"
                                    variant="secondary"
                                    onClick={() => document.getElementById('specification-file-input')?.click()}
                                >
                                    {t('Upload')}
                                </Button>
                            </div>
                        </div>
                        <div className={css.dropdownContainer}>
                            {dataSheet.length > 0 && (
                                <DocumentName
                                    files={dataSheet}
                                    onClick={(file) => handleDocumentRemove(file, setDataSheet)}
                                />
                            )}
                            <span className={css.star}>*</span>
                            {t('Datasheet')}
                            <div className={css.dropdown}>
                                <input
                                    type="file"
                                    id="data-sheet-file-input"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleDocumentAdd(e, setDataSheet)}
                                    multiple={true}
                                    accept='.pdf'
                                />
                                <Button
                                    id="data-sheet-upload-button"
                                    type="button"
                                    variant="secondary"
                                    onClick={() => document.getElementById('data-sheet-file-input')?.click()}
                                >
                                    {t('Upload')}
                                </Button>
                            </div>
                        </div>
                        <div className={css.dropdownContainer}>
                            {pictures.length > 0 && (
                                <DocumentName
                                    files={pictures}
                                    onClick={(file) => handleDocumentRemove(file, setPictures)}
                                />
                            )}
                            <span className={css.star}>*</span>
                            {t('Picture')}
                            <div className={css.dropdown}>
                                <input
                                    type="file"
                                    id="picture-file-input"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleDocumentAdd(e, setPictures, MAX_IMAGE_SIZE_BYTES)}
                                    accept="image/jpeg,image/png,image/svg+xml,image/gif,image/webp"
                                    multiple={true}
                                />
                                <Button
                                    id="picture-upload-button"
                                    type="button"
                                    variant="secondary"
                                    onClick={() => document.getElementById('picture-file-input')?.click()}
                                >
                                    {t('Upload')}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={css.buttonContainer}>
                        <Button
                            id="cancel-button"
                            type="button"
                            variant="primary"
                            className={css.button}
                            onClick={() => navigate(-1)} /* Navigate back */
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            id="send-button"
                            type="submit"
                            variant="primary"
                            className={css.button}
                            isDisabled={isButtonDisabled}
                        >
                            {showSpinner ? (
                                <div className={css.spinner}>
                                    <Spinner size={2} />
                                </div>
                            ) : (
                                t('Send')
                            )}
                        </Button>
                    </div>
                    {uploadProgress && (
                        <LoadingOverlay
                            isOpen={showSpinner}
                            message={uploadProgress}
                            currentFile={currentFileIndex}
                            totalFiles={totalFilesCount}
                        />
                    )}
                </div>
            </form>
            {isCompleteDialogOpen && (
                <CompleteRequestDialog
                    setIsCompleteDialogOpen={setIsCompleteDialogOpen}
                    isErrorDialog={showErrorDialog}
                    headerText={t('Stock equipment')}
                    successText={showErrorDialog ? t('Equipment adding failed') : t('Equipment added successfully')}
                    errorText={t('Something went wrong')}
                    closeButtonText={t('Close')}
                    onClose={() => {
                        if (!showErrorDialog) {
                            navigate('/stock-equipment');
                        }
                    }}
                />
            )}
            {isImageSizeErrorDialogOpen && (
                <CompleteRequestDialog
                    setIsCompleteDialogOpen={setIsImageSizeErrorDialogOpen}
                    isErrorDialog={true}
                    headerText={t('Picture')}
                    successText=""
                    errorText={t('Picture is too large. Please upload a picture smaller than 8 MB.')}
                    closeButtonText={t('Close')}
                />
            )}
        </>
    );
};

const StyledMenuItem = styled(MenuItem)(() => ({
    fontSize: '1.6rem',
}));
const StyledSelect = styled(Select)(() => ({
    width: '100%',
    marginTop: '0.5rem',
    borderRadius: '2rem',
    height: '4rem',
    fontSize: '1.6rem',
    marginBottom: '1rem',
    '& .MuiList-root': {
        fontSize: '1.6rem',
    },
}));

const StyledChevron = styled(Chevron)<{ isOpen?: boolean }>(({ isOpen }) => ({
    transform: isOpen ? 'rotate(90deg)' : 'rotate(-90deg)',
    marginRight: '2rem',
    height: '1.4rem',
    width: '0.9rem',
}));

const StyledDatePicker = styled(DatePicker)(() => ({
    width: '100%',
    marginTop: '0.5rem',
    '& .MuiPickersOutlinedInput-root': {
        borderRadius: '2rem',
        fontSize: '1.6rem',
    },
    '& .MuiPickersSectionList-root': {
        fontSize: '1.6rem',
        padding: '0.9rem',
    },
    '& .MuiSvgIcon-root': {
        width: '2rem',
        height: '2rem',
        color: '#5e2460',
    },

    // TODO: Fixme: fontSize not working
    '& .MuiButtonBase-root-MuiPickersDay-root': {
        fontSize: '1.6rem',
    },
    '& .MuiButtonBase-root': {
        fontSize: '1.6rem',
    },
}));
